import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { Truck } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { ActionButton } from '@/shared/ui/action-button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import type { ReceiveConvoyConfirmResultDto } from '@/entities/receive';
import { ConfirmArrivalSkeleton } from '@/features/receive-confirm-arrival';

import { RECEIVE_CONVOY_FORM_ID } from '../model/constants';
import { useReceiveConvoyForm } from '../model/use-receive-convoy-form';
import type { ReceiveConvoyTarget } from '../model/types';
import { ReceiveConvoyContextError } from './receive-convoy-context-error';
import { ReceiveConvoyHeaderFields } from './receive-convoy-header-fields';
import { ReceiveConvoyLinesTable } from './receive-convoy-lines-table';
import { ReceiveConvoyNotDispatched } from './receive-convoy-not-dispatched';
import { ReceiveConvoyResult } from './receive-convoy-result';

export interface ReceiveConvoyModalProps {
  /** The convoy row being received. Mounted only while one is selected, so every open starts fresh. */
  target: ReceiveConvoyTarget;
  onClose: () => void;
  onSuccess?: (result: ReceiveConvoyConfirmResultDto) => void;
}

export function ReceiveConvoyModal({ target, onClose, onSuccess }: ReceiveConvoyModalProps) {
  const { t } = useTranslation();

  const {
    form,
    context,
    locationsById,
    unitMode,
    setUnitMode,
    result,
    isLoading,
    isSubmitting,
    contextError,
    handleResetQuantities,
    handleSubmit,
    handleClose
  } = useReceiveConvoyForm({ target, onClose, onSuccess });

  const title = context?.dialogTitle || t('receive.receive.convoy.button', 'Receive Convoy');
  const nothingToReceive = context?.shipments.length === 0;

  /**
   * The body as a chain of early returns — a plain function call, not a
   * component, so the form is not remounted on every render. The result comes
   * first: the receipt invalidates the `Receive` tag and refetches this context,
   * and the GRN numbers in the result exist nowhere else.
   */
  const renderBody = () => {
    if (result) return <ReceiveConvoyResult result={result} />;
    if (isLoading) return <ConfirmArrivalSkeleton />;
    if (contextError || !context) return <ReceiveConvoyContextError error={contextError} />;
    if (nothingToReceive) {
      return (
        <div className="space-y-4">
          <Alert>
            <AlertDescription>
              {t('receive.convoy.nothingToReceive', 'Every shipment of this convoy has already been received.')}
            </AlertDescription>
          </Alert>
          <ReceiveConvoyNotDispatched journeys={context.notDispatchedJourneys} />
        </div>
      );
    }

    return (
      <form
        id={RECEIVE_CONVOY_FORM_ID}
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void handleSubmit(event);
        }}
        className="space-y-6">
        <ReceiveConvoyNotDispatched journeys={context.notDispatchedJourneys} />

        <ReceiveConvoyHeaderFields form={form} context={context} />

        <ReceiveConvoyLinesTable
          form={form}
          context={context}
          locationsById={locationsById}
          unitMode={unitMode}
          onUnitModeChange={setUnitMode}
          onResetQuantities={handleResetQuantities}
        />

        <div className="flex items-center gap-2">
          <Controller
            name="printGoodsReceivedNote"
            control={form.control}
            render={({ field }) => (
              <Checkbox id="convoyPrintGoodsReceivedNote" checked={field.value} onCheckedChange={field.onChange} />
            )}
          />
          <Label htmlFor="convoyPrintGoodsReceivedNote" className="text-sm font-normal">
            {t('dispatch.print.goods.receive.note.button', 'Print Goods Received Note')}
          </Label>
        </div>
      </form>
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        // `sm:max-w-…` must carry the prefix to override DialogContent's own
        // `sm:max-w-lg` — see the single receipt modal. Wider than it: the
        // convoy table adds a location column.
        className="grid max-h-[92vh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden sm:max-w-[min(1400px,calc(100%-2rem))]"
        onInteractOutside={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-muted-foreground" aria-hidden />
            {context?.convoyName || target.convoyName
              ? `${title} — ${context?.convoyName || target.convoyName}`
              : title}
          </DialogTitle>
          <DialogDescription>
            {t(
              'receive.convoy.description',
              'Record where, when and in what condition every truck of the convoy arrived.'
            )}
          </DialogDescription>
        </DialogHeader>

        {/* The only vertical scroller, so the title and the buttons stay put.
            `overflow-x-hidden` keeps it from becoming a horizontal scroller too;
            the table scrolls sideways inside its own wrapper. */}
        <div className="min-h-0 overflow-y-auto overflow-x-hidden">{renderBody()}</div>

        <DialogFooter className="gap-2 border-t pt-4 sm:justify-center">
          <ActionButton type="button" intent="dismiss" className="min-w-[140px]" onClick={handleClose}>
            {t('gen.closedialog.label', 'Close Dialog')}
          </ActionButton>
          {!result && (
            <ActionButton
              intent="primary"
              type="submit"
              form={RECEIVE_CONVOY_FORM_ID}
              className="min-w-[140px]"
              disabled={isSubmitting || isLoading || !context || nothingToReceive}>
              {isSubmitting ? <Spinner className="mr-2 h-4 w-4" /> : <Truck className="mr-2 h-4 w-4" />}
              {t('receive.button.label', 'Receive')}
            </ActionButton>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
